import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	AvatarInfo,
	ImageMimeType,
	PresignAvatarRequest,
	UserAvatarProxy,
} from '@entities/User'
import { userQueryKeys } from '@entities/User/config/query-keys'

interface UseUploadAvatarResult {
	status: 'idle' | 'loading' | 'success' | 'error'
	error: string | null
	progress: number
	uploadAvatar: (file: File) => Promise<AvatarInfo | null>
	newAvatar: null | AvatarInfo
}

async function pollForAvatar(
	expectedAvatarId: string,
	timeout: number = 10000,
	interval: number = 500
): Promise<AvatarInfo | null> {
	const startTime = Date.now()
	const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
	while (Date.now() - startTime < timeout) {
		try {
			const avatarResult: AvatarInfo | null = await UserAvatarProxy.getCurrentAvatar()
			if (avatarResult?.id === expectedAvatarId) {
				return avatarResult
			}
		} catch (error) {
			console.error('Error during avatar fetch:', error)
		}
		await delay(interval)
	}
	return null
}

const getErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message
	}
	return 'An unknown error occurred during upload.'
}

export const useUploadImage = (): UseUploadAvatarResult => {
	const queryClient = useQueryClient()
	const [error, setError] = useState<string | null>(null)
	const [progress, setProgress] = useState<number>(0)
	const [newAvatar, setNewAvatar] = useState<AvatarInfo | null>(null)

	const uploadMutation = useMutation({
		mutationFn: async (file: File): Promise<AvatarInfo> => {
			setError(null)
			setProgress(0)

			const presignRequest: PresignAvatarRequest = {
				filename: file.name,
				mime_type: file.type as ImageMimeType,
				ttl_minutes: 60,
			}

			const presignResponse = await UserAvatarProxy.presignImage(presignRequest)

			if (!presignResponse?.upload_url) {
				throw new Error('Failed to get presigned URL from server.')
			}

			setProgress(33)

			const uploadResult = await UserAvatarProxy.uploadImageToS3(
				presignResponse.upload_url,
				file
			)

			if (!uploadResult) {
				throw new Error('Failed to upload file to S3.')
			}

			const avatarResult: AvatarInfo | null = await pollForAvatar(
				presignResponse.id,
				10000,
				500
			)
			if (!avatarResult) {
				throw new Error('Avatar is still being processed.')
			}
			setProgress(66)
			setNewAvatar(avatarResult)

			setProgress(100)
			return avatarResult
		},
		onSuccess: avatar => {
			queryClient.setQueryData(userQueryKeys.currentAvatar, avatar)
		},
		onError: (mutationError: unknown) => {
			setError(getErrorMessage(mutationError))
		},
	})
	const { mutateAsync, status } = uploadMutation

	const uploadAvatar = useCallback(async (file: File): Promise<AvatarInfo | null> => {
		try {
			return await mutateAsync(file)
		} catch (mutationError: unknown) {
			setError(getErrorMessage(mutationError))
			return null
		}
	}, [mutateAsync])

	return {
		status,
		error,
		progress,
		uploadAvatar,
		newAvatar,
	}
}
